import { envConfig } from '../config/authConfig';
import {
  PeerFeedbackDetail,
  PeerFeedbackSummary,
  PeerNotificationSummary,
  RespondToPeerFeedback,
  SendPeerFeedbackRequest,
  UserSearchResult,
} from '../types/peerFeedback';

const BASE = envConfig.apiBaseUrl;

type ApiCall = (url: string, options?: RequestInit) => Promise<Response>;

async function unwrap<T>(res: Response): Promise<T> {
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json.errors?.[0] ?? json.message ?? 'Request failed');
  }
  return json.data as T;
}

// ── User search (Graph API via backend) ──────────────────────────────────────

export async function searchUsers(
  q: string,
  apiCall: ApiCall
): Promise<UserSearchResult[]> {
  const res = await apiCall(`${BASE}/api/usersearch/search?q=${encodeURIComponent(q)}`);
  return unwrap<UserSearchResult[]>(res);
}

// ── Send feedback request ─────────────────────────────────────────────────────

export async function sendPeerFeedbackRequest(
  dto: SendPeerFeedbackRequest,
  apiCall: ApiCall
): Promise<number[]> {
  const res = await apiCall(`${BASE}/api/peerfeedback/request`, {
    method: 'POST',
    body: JSON.stringify(dto),
  });
  return unwrap<number[]>(res);
}

// ── Get tabs data ─────────────────────────────────────────────────────────────

export interface PeerFeedbackFilters {
  year?: number;
  month?: number;
}

function buildFilterQuery(filters?: PeerFeedbackFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.year != null) params.set('year', String(filters.year));
  if (filters.month != null) params.set('month', String(filters.month));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function getFeedbackReceived(
  requesterId: string,
  apiCall: ApiCall,
  filters?: PeerFeedbackFilters
): Promise<PeerFeedbackSummary[]> {
  const res = await apiCall(
    `${BASE}/api/peerfeedback/received/${encodeURIComponent(requesterId)}${buildFilterQuery(filters)}`
  );
  return unwrap<PeerFeedbackSummary[]>(res);
}

export async function getFeedbackRequested(
  requesterId: string,
  apiCall: ApiCall,
  filters?: PeerFeedbackFilters
): Promise<PeerFeedbackSummary[]> {
  const res = await apiCall(
    `${BASE}/api/peerfeedback/requested/${encodeURIComponent(requesterId)}${buildFilterQuery(filters)}`
  );
  return unwrap<PeerFeedbackSummary[]>(res);
}

export async function getFeedbackProvided(
  recipientId: string,
  apiCall: ApiCall,
  filters?: PeerFeedbackFilters
): Promise<PeerFeedbackSummary[]> {
  const res = await apiCall(
    `${BASE}/api/peerfeedback/provided/${encodeURIComponent(recipientId)}${buildFilterQuery(filters)}`
  );
  return unwrap<PeerFeedbackSummary[]>(res);
}

export async function getPendingActions(
  recipientId: string,
  apiCall: ApiCall,
  filters?: PeerFeedbackFilters
): Promise<PeerFeedbackSummary[]> {
  const res = await apiCall(
    `${BASE}/api/peerfeedback/pending/${encodeURIComponent(recipientId)}${buildFilterQuery(filters)}`
  );
  return unwrap<PeerFeedbackSummary[]>(res);
}

export async function getPeerFeedbackDetail(
  id: number,
  apiCall: ApiCall
): Promise<PeerFeedbackDetail> {
  const res = await apiCall(`${BASE}/api/peerfeedback/${id}`);
  return unwrap<PeerFeedbackDetail>(res);
}

// ── Actions ───────────────────────────────────────────────────────────────────

export async function respondToPeerFeedback(
  requestId: number,
  dto: RespondToPeerFeedback,
  apiCall: ApiCall
): Promise<void> {
  const res = await apiCall(`${BASE}/api/peerfeedback/${requestId}/respond`, {
    method: 'POST',
    body: JSON.stringify(dto),
  });
  await unwrap<unknown>(res);
}

export async function sendReminder(
  requestId: number,
  requesterId: string,
  apiCall: ApiCall
): Promise<void> {
  const res = await apiCall(
    `${BASE}/api/peerfeedback/${requestId}/remind?requesterId=${encodeURIComponent(requesterId)}`,
    { method: 'POST' }
  );
  await unwrap<unknown>(res);
}

// ── Notifications ─────────────────────────────────────────────────────────────

export async function getPeerNotifications(
  userId: string,
  apiCall: ApiCall
): Promise<PeerNotificationSummary> {
  const res = await apiCall(`${BASE}/api/peerfeedback/notifications/${encodeURIComponent(userId)}`);
  return unwrap<PeerNotificationSummary>(res);
}

export async function markPeerNotificationsRead(
  userId: string,
  apiCall: ApiCall
): Promise<void> {
  const res = await apiCall(
    `${BASE}/api/peerfeedback/notifications/${encodeURIComponent(userId)}/read`,
    { method: 'PUT' }
  );
  await unwrap<unknown>(res);
}

export async function deletePeerNotification(
  userId: string,
  id: number,
  apiCall: ApiCall
): Promise<void> {
  const res = await apiCall(
    `${BASE}/api/peerfeedback/notifications/${encodeURIComponent(userId)}/${id}`,
    { method: 'DELETE' }
  );
  await unwrap<unknown>(res);
}
